import React, { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, RefreshCw, Send, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Weekly digest review dashboard.
 *
 * Admin workflow:
 *   1. Click "Generate this week's drafts" → backend loops through every
 *      user with a Kundli and prepares a personalised markdown draft using
 *      that customer's chart. Requires OTP verification.
 *   2. Review each draft → inline edit subject/heading/body → save.
 *   3. Send individual OR "Send all pending" for bulk delivery.
 *
 * Drafts persist across sessions until sent (or overwritten by the next
 * Generate click), so the admin can walk away mid-review and come back.
 */
export default function WeeklyDigest() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [selected, setSelected] = useState(null); // draft.id being edited

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/email/weekly-digest`, { withCredentials: true })
      .then(r => setDrafts(r.data || []))
      .catch(() => setDrafts([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!window.confirm("Generate weekly drafts? This queries the AI once per user with a Kundli — may take 30-60 seconds.")) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/email/weekly-digest/generate`, {}, { withCredentials: true });
      alert(`Created ${data.drafts_created} drafts for ${data.week_label}.`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Draft generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const sendAll = async () => {
    if (!window.confirm("Send ALL pending weekly drafts now?")) return;
    setBulk(true);
    try {
      const { data } = await axios.post(`${API}/admin/email/weekly-digest/send-all`, {}, { withCredentials: true });
      alert(`Sent ${data.sent}, failed ${data.failed}.`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Bulk send failed.");
    } finally {
      setBulk(false);
    }
  };

  const sendOne = async (id) => {
    if (!window.confirm("Send this draft now?")) return;
    try {
      await axios.post(`${API}/admin/email/weekly-digest/${id}/send`, {}, { withCredentials: true });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Send failed.");
    }
  };

  const saveEdit = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/email/weekly-digest/${id}`, patch, { withCredentials: true });
      setSelected(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed.");
    }
  };

  const pending = drafts.filter(d => d.status === "pending");
  const sent = drafts.filter(d => d.status === "sent");

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "18px", flexWrap: "wrap", gap: "10px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Calendar size={20} style={{ color: "#FFD166" }} />
            <h2 className="font-cinzel" style={{ fontSize: "1.15rem", color: "#FFD166" }}>Weekly Digest — Review &amp; Send</h2>
          </div>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: "4px" }}>
            Personalised weekly forecasts for every customer with a Kundli. Review, edit, and approve before delivery.
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button data-testid="digest-generate-btn" disabled={generating} onClick={generate} className="btn-ghost" style={{ padding: "9px 16px", fontSize: "0.82rem" }}>
            <RefreshCw size={13} style={{ display: "inline", marginRight: 6 }} />
            {generating ? "Generating…" : "Generate this week's drafts"}
          </button>
          {pending.length > 0 && (
            <button data-testid="digest-send-all-btn" disabled={bulk} onClick={sendAll} className="btn-gold" style={{ padding: "9px 16px", fontSize: "0.82rem" }}>
              <Send size={13} style={{ display: "inline", marginRight: 6 }} />
              {bulk ? "Sending…" : `Send all ${pending.length} pending`}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ color: "rgba(255,255,255,0.4)", padding: "40px" }}>Loading drafts…</div>
      ) : drafts.length === 0 ? (
        <div className="glass-card" style={{ padding: "40px", textAlign: "center", color: "rgba(255,255,255,0.5)" }}>
          No drafts yet. Click <b style={{ color: "#FFD166" }}>Generate this week&apos;s drafts</b> to create personalised forecasts for every customer with a Kundli.
        </div>
      ) : (
        <>
          <div style={{ color: "#c4b5fd", fontSize: "0.82rem", marginBottom: "10px" }}>
            {pending.length} pending · {sent.length} sent
          </div>
          {drafts.map(d => (
            <DraftCard
              key={d.id}
              draft={d}
              isEditing={selected === d.id}
              onEdit={() => setSelected(d.id)}
              onCancel={() => setSelected(null)}
              onSave={(patch) => saveEdit(d.id, patch)}
              onSend={() => sendOne(d.id)}
            />
          ))}
        </>
      )}
    </div>
  );
}

function DraftCard({ draft, isEditing, onEdit, onCancel, onSave, onSend }) {
  const [subject, setSubject] = useState(draft.subject || "");
  const [heading, setHeading] = useState(draft.heading || "");
  const [body, setBody] = useState(draft.body_markdown || "");

  useEffect(() => {
    setSubject(draft.subject || "");
    setHeading(draft.heading || "");
    setBody(draft.body_markdown || "");
  }, [draft.id, isEditing]);

  const isSent = draft.status === "sent";
  return (
    <div className="glass-card" data-testid={`digest-draft-${draft.id}`} style={{ padding: "16px 18px", marginBottom: "12px", border: `1px solid ${isSent ? "rgba(34,197,94,0.28)" : "rgba(255,209,102,0.16)"}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "10px" }}>
        <div>
          <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}>{draft.user_name || draft.user_email}</div>
          <div style={{ color: "#A89CB8", fontSize: "0.78rem" }}>{draft.user_email}</div>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <span style={{ background: isSent ? "rgba(34,197,94,0.14)" : "rgba(255,209,102,0.14)", color: isSent ? "#86efac" : "#FFD166", padding: "3px 10px", borderRadius: "999px", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" }}>
            {isSent ? `Sent · ${(draft.sent_at || "").slice(0, 10)}` : "Pending"}
          </span>
          {!isSent && !isEditing && (
            <>
              <button onClick={onEdit} className="btn-ghost" style={{ padding: "5px 12px", fontSize: "0.76rem" }}>Edit</button>
              <button onClick={onSend} className="btn-gold" style={{ padding: "5px 12px", fontSize: "0.76rem" }}>
                <Send size={11} style={{ display: "inline", marginRight: 4 }} /> Send
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div>
          <div style={{ marginBottom: "8px" }}>
            <label style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", display: "block", marginBottom: "3px" }}>Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} className="cosmic-input" />
          </div>
          <div style={{ marginBottom: "8px" }}>
            <label style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", display: "block", marginBottom: "3px" }}>Headline</label>
            <input value={heading} onChange={e => setHeading(e.target.value)} className="cosmic-input" />
          </div>
          <div style={{ marginBottom: "10px" }}>
            <label style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", display: "block", marginBottom: "3px" }}>Body (markdown)</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={7} className="cosmic-input" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.82rem" }} />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button data-testid={`digest-save-${draft.id}`} onClick={() => onSave({ subject, heading, body_markdown: body })} className="btn-gold" style={{ padding: "7px 14px", fontSize: "0.78rem" }}>
              <Save size={12} style={{ display: "inline", marginRight: 5 }} /> Save
            </button>
            <button onClick={onCancel} className="btn-ghost" style={{ padding: "7px 14px", fontSize: "0.78rem" }}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ color: "#FFD166", fontWeight: 600, fontSize: "0.9rem", marginBottom: "4px" }}>{draft.subject}</div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.83rem", lineHeight: 1.6, whiteSpace: "pre-wrap", maxHeight: "160px", overflowY: "auto", background: "rgba(0,0,0,0.25)", borderRadius: "8px", padding: "10px 12px" }}>
            {draft.body_markdown}
          </div>
        </>
      )}
    </div>
  );
}
